import { useActions, useAppSelector } from '@/hooks'
import {
  useGetDeviceDetailQuery,
  useGetPhotosDetailQuery,
} from '@/modules/device-module/services/deviceService'
import { stateDeviceSelector } from '@/modules/device-module/store/stateSlice'
import { useGetUserProfileQuery } from '@/modules/user/services/userService'
import { DeviceType } from '@/types/enums'
import { DeviceTab } from '@/modules/device-module/store/stateSlice'
import { UserTypes } from '@/types/typeUser'
import cn from '@/utils/cn'
import { Box, Tabs } from '@mui/material'
import Tab from '@mui/material/Tab'
import * as React from 'react'
import { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import ActionsIcons from './ActionsIcons'

const tabsClassName = cn('device-tabs')

const TAB_STYLE = { lineHeight: 1.7 }

type DeviceDetailNullable = Awaited<
  ReturnType<typeof useGetDeviceDetailQuery>
>['data']
type PhotosDetailNullable = Awaited<
  ReturnType<typeof useGetPhotosDetailQuery>
>['data']

const isMediaDevice = (
  data: DeviceDetailNullable,
  photosData: PhotosDetailNullable
) =>
  data?.typeId === DeviceType.photo ||
  data?.typeId === DeviceType.video ||
  data?.typeId === DeviceType.dit ||
  photosData?.typeId === DeviceType.photo

const getInitialTab = (
  data: DeviceDetailNullable,
  state: unknown,
  currentUserType: number | undefined,
  selectedTab: number
): number => {
  if (data?.typeId === DeviceType.detector) return DeviceTab.devices
  if (data?.typeId === DeviceType.dtp) return DeviceTab.properties
  if (
    data &&
    !data.smart &&
    data.typeId !== DeviceType.photo &&
    data.typeId !== DeviceType.video &&
    data.typeId !== DeviceType.dit
  ) {
    if (currentUserType !== UserTypes.superAdmin) {
      return isMediaDevice(data, null)
        ? DeviceTab.properties
        : DeviceTab.description
    }
    return DeviceTab.properties
  }
  const stateObj = state as { eventId?: number; deviceId?: number } | null
  if (stateObj?.eventId && stateObj?.deviceId) return DeviceTab.events
  return selectedTab
}

const getDevicesTabLabel = (
  data: DeviceDetailNullable,
  photosData: PhotosDetailNullable
): string => {
  if (data?.typeId === DeviceType.meteo) return 'Датчики'
  if (data?.typeId === DeviceType.detector) return 'Устройство'
  if (isMediaDevice(data, photosData)) return 'Изображение'
  return 'Устройства'
}

const getTabVisibility = (
  data: DeviceDetailNullable,
  photosData: PhotosDetailNullable,
  currentUserType: number | undefined
) => {
  const media = isMediaDevice(data, photosData)
  const smart = Boolean(data?.smart)
  const isDetector = data?.typeId === DeviceType.detector
  const detectorNotUser = isDetector && currentUserType !== UserTypes.user
  return {
    devices: smart || media || isDetector,
    forecast: false,
    events:
      smart ||
      media ||
      detectorNotUser ||
      data?.typeId === DeviceType.lightning ||
      (data?.typeId === DeviceType.dtp && currentUserType !== UserTypes.user) ||
      false,
    description: ![
      DeviceType.dtp,
      DeviceType.dtpYellow,
      DeviceType.dtpRed,
    ].includes(data?.typeId ?? 0),
    properties:
      data?.typeId === DeviceType.lightning ||
      detectorNotUser ||
      currentUserType === UserTypes.superAdmin,
    terminal: smart && currentUserType === UserTypes.superAdmin,
  }
}

const Actions = () => {
  const { data: currentUserData } = useGetUserProfileQuery('0')
  const currentUserType = currentUserData?.data.typeId

  const { id } = useParams()
  const { pathname, state } = useLocation()

  const { selectedTab } = useAppSelector(stateDeviceSelector)
  const { setSelectedTab, setFilters } = useActions()

  const { data } = useGetDeviceDetailQuery(Number(id), {
    skip: pathname.includes('photos'),
  })
  const { data: photosData } = useGetPhotosDetailQuery(Number(id), {
    skip: !pathname.includes('photos'),
  })

  const visibility = getTabVisibility(data, photosData, currentUserType)
  const devicesLabel = getDevicesTabLabel(data, photosData)

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setFilters([0])
  }

  useEffect(() => {
    const initialTab = getInitialTab(data, state, currentUserType, selectedTab)
    setSelectedTab(initialTab)
  }, [data, id, state, currentUserType, setSelectedTab])

  const tabs = [
    { label: devicesLabel, visible: visibility.devices },
    { label: 'Прогноз', visible: visibility.forecast },
    { label: 'События', visible: visibility.events },
    { label: 'Описание', visible: visibility.description },
    { label: 'Свойства', visible: visibility.properties },
    { label: 'Терминал', visible: visibility.terminal },
  ]

  return (
    <Box
      display='flex'
      justifyContent='space-between'
      flexDirection={{ xss: 'column-reverse', xlg: 'row' }}
      alignItems={{ xss: 'center', xlg: 'flex-start' }}
      gap={{ xss: 0, lg: 0 }}
    >
      <ActionsIcons />
      <Box
        sx={{
          maxWidth: {
            xss: 300,
            xs: 360,
            sm: 420,
            md: 500,
            lg: 500,
            xlg: 560,
            xl3: 750,
          },
        }}
      >
        <Tabs
          className={tabsClassName()}
          value={selectedTab}
          onChange={handleChange}
          variant='scrollable'
          scrollButtons='auto'
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              sx={{
                display: tab.visible ? 'block' : 'none',
                ...TAB_STYLE,
              }}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  )
}

export default Actions
